import { useCallback, useEffect, useRef } from 'react';
import type { QueryKey } from 'react-query';
import { useQueryClient } from 'react-query';

type UseQueryPollingParams = {
  queryKey: QueryKey;
  durationMs?: number;
  intervalMs?: number;
};

export const useQueryPolling = ({
  queryKey,
  durationMs = 5000,
  intervalMs = 500,
}: UseQueryPollingParams) => {
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const start = useCallback(() => {
    stop();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const deadline = Date.now() + durationMs;

    const loop = async () => {
      while (Date.now() < deadline && !controller.signal.aborted) {
        await queryClient.invalidateQueries(queryKey);
        if (controller.signal.aborted) break;
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    };

    void loop();
  }, [queryClient, queryKey, durationMs, intervalMs, stop]);

  useEffect(() => stop, [stop]);

  return { start, stop };
};
